// Landing page — D1 golden sample (mockup market/landing-ai.jsx NLandingAI +
// heroes.jsx HeroB). Static marketing; no backend, no form submit (the
// command bar replays canned example answers; demo CTAs open the contact form
// with the demo topic preselected). Sections, in the drawing's order:
//   1. Hero — Outfit-display headline + AI command bar + trust logos
//   2. AI capabilities — four cards (what Novem AI does, and what it does not)
//   3. How it works — three steps from first meeting to go-live
//   4. Modules — the six-module ground the AI runs on
//   5. Editorial quote + product properties (no figures: there is no measured
//      customer data to quote)
//   6. CTA band — emerald-950 closer
//
// Truthfulness (docs/tanitim/novem-hr-tanitim.html is the standard): a section
// exists here only if the product does what it says. The 08:00 daily digest and
// the payroll what-if simulation were removed because no code ships them; do
// not re-add either without a shipped feature behind it.

// Token disiplini (CLAUDE.md): her renk bir spec-44 değişkenidir. Maketin
// --emerald-* / --oat-* / durum tonları anlamsal ROLLERE bağlanır (--accent,
// --bg-*, --state-*) ya da globals.css'teki --mkt-* takma adlarına. Sabit
// rampa basamağı (--color-*-NNN) yalnız iki temada da koyu kalan derin
// bantlarda kullanılır; başka yerde tema ile dönmez ve koyu temayı kırar.
// Display yüzü (Outfit) başlıklara ve istatistik rakamlarına ayrılmıştır.

import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  BookOpen,
  CalendarDays,
  Inbox,
  Loader2,
  Power,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Button } from "@novemai/components";

import { AIChip, DisplayHeading, Eyebrow } from "@/components/marketing-primitives";
import { DEMO_REQUEST_PATH } from "@/lib/site-links";
import { cn } from "@/lib/utils";

// ── Status tint helper ──────────────────────────────────────────────
// The mockup keys rows by a status word; map each to its spec-44 tint pair
// (background + foreground) so no raw hex leaks into the markup.
type StatusTone = "success" | "warning" | "info" | "danger";
const TONE_BG: Record<StatusTone, string> = {
  success: "bg-[var(--mkt-tint-success)]",
  warning: "bg-[var(--mkt-tint-warning)]",
  info: "bg-[var(--mkt-tint-info)]",
  danger: "bg-[var(--mkt-tint-danger)]",
};
const TONE_FG: Record<StatusTone, string> = {
  success: "text-[var(--state-success)]",
  warning: "text-[var(--state-warning)]",
  info: "text-[var(--state-info)]",
  danger: "text-[var(--state-danger)]",
};
// Solid status dot (the absence-answer table) — needs the saturated -500 step,
// not the -50 tint TONE_BG carries.
const TONE_DOT: Record<StatusTone, string> = {
  success: "bg-[var(--color-success-500)]",
  warning: "bg-[var(--color-warning-500)]",
  info: "bg-[var(--color-info-500)]",
  danger: "bg-[var(--color-danger-500)]",
};

// ════════════════════════════════════════════════════════════════════
// 1. HERO — interactive "Ask Novem AI" command bar
// ════════════════════════════════════════════════════════════════════
// The mockup's command bar (heroes.jsx HeroB) had a static "Sor" button. Here
// it actually DOES something: pressing Ask — or a suggestion chip — runs a short
// "thinking" beat, then reveals an EXAMPLE answer screen (the spirit of the
// mockup's AssistantMock). Two canned queries, two answer shapes. Everything
// is illustrative: each answer carries an "example answer" pill + a disclaimer,
// and the numbers are NOT presented as real customer data or platform metrics
// (İSTON-class honesty rule, FRONTEND_PORT_MAP §10). No backend, no network.
//
// The staged questions are the ones the brochure itself uses: absence is its
// verbatim example (NOTE: the report engine has no attendance dataset yet, so
// that answer is a gap between brochure and code, tracked with the owner) and
// headcount by department is what the directory dataset really answers. The
// earlier social-security / turnover demos were dropped: no reporting dataset
// carries an SGK premium or a turnover column. The inert "detail / suggest
// action" buttons went with them, since the assistant reads and reports and
// never proposes or executes an action.

type DemoKey = "absence" | "headcount";
const DEMO_KEYS: readonly DemoKey[] = ["absence", "headcount"];

// Each demo query echoes an EXISTING command i18n string, so a given query text
// renders exactly once (active query in the bar; the rest as chips) — no
// duplicate-text collisions for the App.test getByText assertion.
const QUERY_KEY: Record<DemoKey, string> = {
  absence: "landing.hero.command.example_query",
  headcount: "landing.hero.command.suggestions.headcount",
};

// Structure lives in code (status tone / bar geometry); the display strings come
// from i18n via returnObjects — index-aligned with these arrays.
const ABSENCE_TONES: readonly StatusTone[] = ["danger", "warning", "success", "success"];
// Index-safety fallback for the tone lookup (noUncheckedIndexedAccess). Hoisted
// out of the render so the tone string isn't flagged as a user-facing literal.
const FALLBACK_TONE: StatusTone = "success";
const HEADCOUNT_SHARE: readonly number[] = [38, 28, 17, 11, 6]; // share-bar widths (%)

const DEMO_BASE = "landing.hero.command.demo";

// ── Example-answer bodies (one shape per query) ─────────────────────
function AbsenceAnswer(): React.ReactElement {
  const { t } = useTranslation();
  const base = `${DEMO_BASE}.absence`;
  const headers = t(`${base}.headers`, { returnObjects: true }) as string[];
  const rows = t(`${base}.rows`, { returnObjects: true }) as string[][];
  return (
    <>
      <p className="mb-3.5 font-serif text-[14.5px] leading-relaxed text-[var(--fg-primary)]">
        {t(`${base}.summary`)}
      </p>
      <div className="overflow-hidden rounded-[10px] border border-[var(--border-subtle)]">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[var(--mkt-panel)]">
              {headers.map((h, i) => (
                <th
                  key={h}
                  className={cn(
                    "px-3 py-2 font-sans text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--fg-muted)]",
                    i === headers.length - 1 ? "text-right" : "text-left",
                  )}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const tone = ABSENCE_TONES[i] ?? FALLBACK_TONE;
              return (
                <tr
                  key={row[0]}
                  className={i > 0 ? "border-t border-[var(--border-subtle)]" : undefined}
                >
                  <td className="px-3 py-2 font-sans text-[13px] font-medium text-[var(--fg-primary)]">
                    {row[0]}
                  </td>
                  <td className="px-3 py-2 font-mono text-[12.5px] text-[var(--fg-secondary)]">
                    {row[1]}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span
                      className={cn("inline-block h-2 w-2 rounded-full", TONE_DOT[tone])}
                      aria-hidden="true"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function HeadcountAnswer(): React.ReactElement {
  const { t } = useTranslation();
  const base = `${DEMO_BASE}.headcount`;
  const rows = t(`${base}.rows`, { returnObjects: true }) as string[][];
  return (
    <>
      <p className="mb-3.5 font-serif text-[14.5px] leading-relaxed text-[var(--fg-primary)]">
        {t(`${base}.summary`)}
      </p>
      <div className="flex flex-col gap-2.5">
        {rows.map((row, i) => (
          <div key={row[0]} className="flex items-center gap-3">
            <span className="w-[34%] shrink-0 truncate font-sans text-[13px] font-medium text-[var(--fg-primary)]">
              {row[0]}
            </span>
            <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-[var(--mkt-panel)]">
              <div
                className="absolute left-0 top-0 h-full rounded-full bg-[var(--accent)]"
                style={{ width: `${HEADCOUNT_SHARE[i] ?? 0}%` }}
              />
            </div>
            <span className="w-[56px] shrink-0 text-right font-mono text-[12.5px] text-[var(--fg-secondary)]">
              {row[1]}
            </span>
            <span className="w-[38px] shrink-0 text-right font-mono text-[12px] text-[var(--fg-muted)]">
              {row[2]}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

function AnswerBody({ activeKey }: { readonly activeKey: DemoKey }): React.ReactElement {
  switch (activeKey) {
    case "absence":
      return <AbsenceAnswer />;
    case "headcount":
      return <HeadcountAnswer />;
  }
}

// ── Answer card chrome + reveal ─────────────────────────────────────
function DemoAnswerCard({ activeKey }: { readonly activeKey: DemoKey }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-[16px] border border-[var(--border-default)] bg-[var(--bg-raised)] text-left shadow-[var(--shadow-lg)]">
      <div className="flex items-center gap-2.5 border-b border-[var(--border-subtle)] bg-[var(--mkt-panel)] px-4 py-2.5">
        <Sparkles size={15} aria-hidden="true" className="text-[var(--accent)]" />
        <span className="font-sans text-[13px] font-semibold text-[var(--accent-hover)]">
          {t(`${DEMO_BASE}.byline`)}
        </span>
        <span className="ml-auto rounded-full bg-[var(--bg-raised)] px-2.5 py-0.5 font-sans text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--fg-muted)]">
          {t(`${DEMO_BASE}.example_pill`)}
        </span>
      </div>
      <div className="px-5 py-4">
        <AnswerBody activeKey={activeKey} />
      </div>
      <div className="border-t border-[var(--border-subtle)] px-5 py-2.5">
        <p className="font-serif text-[12px] italic leading-snug text-[var(--fg-muted)]">
          {t(`${DEMO_BASE}.disclaimer`)}
        </p>
      </div>
    </div>
  );
}

function ThinkingCard({ label }: { readonly label: string }): React.ReactElement {
  return (
    <div className="flex items-center gap-3 rounded-[14px] border border-[var(--border-subtle)] bg-[var(--bg-raised)] px-4 py-3.5 shadow-[var(--shadow-sm)]">
      <Loader2
        size={16}
        aria-hidden="true"
        className="shrink-0 animate-spin text-[var(--accent)] motion-reduce:animate-none"
      />
      <span className="font-serif text-[14px] italic text-[var(--fg-muted)]">{label}</span>
    </div>
  );
}

// One-tick entrance: re-keyed by the active query so each new answer slides in.
function Reveal({
  revealKey,
  children,
}: {
  readonly revealKey: string;
  readonly children: React.ReactNode;
}): React.ReactElement {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    setShown(false);
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, [revealKey]);
  return (
    <div
      className={cn(
        "transition-all duration-500 ease-out motion-reduce:transition-none",
        shown ? "translate-y-0 opacity-100" : "translate-y-1.5 opacity-0",
      )}
    >
      {children}
    </div>
  );
}

type DemoPhase = "idle" | "thinking" | "answered";

function Hero(): React.ReactElement {
  const { t } = useTranslation();
  // Headcount opens first: the directory dataset really answers it, while the
  // report engine has no attendance dataset behind the absence example.
  const [activeKey, setActiveKey] = useState<DemoKey>("headcount");
  const [phase, setPhase] = useState<DemoPhase>("idle");
  const timer = useRef<number | null>(null);

  const ask = useCallback((key: DemoKey) => {
    setActiveKey(key);
    setPhase("thinking");
    if (timer.current !== null) window.clearTimeout(timer.current);
    // Short cosmetic beat before the answer resolves — sells "AI is working".
    timer.current = window.setTimeout(() => setPhase("answered"), 650);
  }, []);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  // Compliance / capability strip — TRUTHFUL product attributes (spec-backed:
  // KVKK data lifecycle ADR-011, regional residency + on-prem ADR-004, 4857 payroll-tr).
  // NOT customer logos: NovemAI has no public reference customers yet, and the
  // design-source tenant is not a customer (see FRONTEND_PORT_MAP). Swap in real
  // reference logos here once they exist + consent. No e-Fatura item: the
  // internal e-Fatura product only invoices NovemAI's own subscriptions and is
  // not an HR feature, so it was never a property of this product.
  const trustItems = [
    t("landing.hero.trust_items.kvkk"),
    t("landing.hero.trust_items.hosting"),
    t("landing.hero.trust_items.labor_law"),
    t("landing.hero.trust_items.deployment"),
  ];

  // Chips = every query EXCEPT the one currently shown in the bar, so each
  // query text appears exactly once across the bar + chip row.
  const otherKeys = DEMO_KEYS.filter((k) => k !== activeKey);

  return (
    <section className="border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]">
      <div className="mx-auto max-w-[920px] px-6 py-20 text-center lg:px-8 lg:py-24">
        <div className="flex justify-center">
          <AIChip>{t("landing.hero.chip")}</AIChip>
        </div>
        <h1 className="mx-auto mt-6 max-w-[760px] font-display text-[clamp(2.75rem,6vw,4.5rem)] font-semibold leading-[1.02] tracking-[-0.038em] text-[var(--fg-primary)]">
          {t("landing.hero.title_line1")}
          <br />
          <span className="font-serif font-normal italic text-[var(--accent)]">
            {t("landing.hero.title_accent")}
          </span>
          {t("landing.hero.title_after_accent")}
        </h1>
        <p className="mx-auto mt-7 max-w-[560px] font-serif text-lg leading-relaxed text-[var(--fg-secondary)]">
          {t("landing.hero.subtitle")}
        </p>

        {/* Command bar — interactive: Ask reveals an example answer below */}
        <div className="mx-auto mt-9 max-w-[620px]">
          <div className="flex items-center gap-3 rounded-[14px] border border-[var(--border-default)] bg-[var(--bg-raised)] p-4 shadow-[var(--shadow-lg)]">
            <Sparkles
              size={18}
              aria-hidden="true"
              className="shrink-0 text-[var(--accent)]"
            />
            <span className="flex-1 text-left font-serif text-base text-[var(--fg-primary)]">
              {t(QUERY_KEY[activeKey])}
            </span>
            <Button
              variant="primary"
              size="sm"
              className="shrink-0"
              onClick={() => ask(activeKey)}
            >
              {t("landing.hero.command.ask")}
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {otherKeys.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => ask(k)}
                className="rounded-full border border-[var(--border-subtle)] bg-[var(--bg-raised)] px-3.5 py-1.5 font-sans text-[13px] text-[var(--fg-secondary)] transition-colors hover:border-[var(--accent)] hover:text-[var(--fg-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
              >
                {t(QUERY_KEY[k])}
              </button>
            ))}
          </div>

          {/* Example-answer reveal. The aria-live region is ALWAYS mounted (only
              its content is gated) so screen readers reliably announce the first
              "thinking" state and the answer that follows — a region created in
              the same render as its content may not announce. `empty:` collapses
              the top margin while idle so there's no phantom gap. */}
          <div className="mt-5 empty:mt-0" aria-live="polite">
            {phase === "thinking" && <ThinkingCard label={t(`${DEMO_BASE}.thinking`)} />}
            {phase === "answered" && (
              <Reveal revealKey={activeKey}>
                <DemoAnswerCard activeKey={activeKey} />
              </Reveal>
            )}
          </div>
        </div>

        <div className="mt-11">
          <p className="font-sans text-[12.5px] font-semibold uppercase tracking-[0.08em] text-[var(--fg-muted)]">
            {t("landing.hero.trusted_by")}
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-9 gap-y-3">
            {trustItems.map((item) => (
              <span
                key={item}
                className="inline-flex items-center gap-2 font-sans text-sm font-medium text-[var(--fg-secondary)]"
              >
                <BadgeCheck
                  size={16}
                  aria-hidden="true"
                  className="text-[var(--accent)]"
                />
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════
// 2. AI CAPABILITIES
// ════════════════════════════════════════════════════════════════════
interface CapItem {
  readonly key: string;
  readonly Icon: LucideIcon;
  readonly tone: StatusTone;
}
// Exactly what ships: the report question, the guideline assistant, and the two
// control properties a regulated buyer asks about first. Triage, payroll
// explainer and approval automation were dropped; no capability of that kind is
// registered on the AI stream.
const CAP_ITEMS: readonly CapItem[] = [
  { key: "reports", Icon: BarChart3, tone: "success" },
  { key: "assistant", Icon: BookOpen, tone: "info" },
  { key: "scope", Icon: ShieldCheck, tone: "warning" },
  { key: "control", Icon: Power, tone: "success" },
];

function Capabilities(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <section className="border-b border-[var(--border-subtle)] bg-[var(--bg-raised)] px-6 py-[72px] lg:px-8">
      <div className="mx-auto mb-12 max-w-[720px] text-center">
        <Eyebrow className="mb-3.5">{t("landing.capabilities.eyebrow")}</Eyebrow>
        <DisplayHeading
          lead={t("landing.capabilities.title_lead")}
          accent={t("landing.capabilities.title_accent")}
          className="text-[clamp(2rem,4vw,2.75rem)]"
        />
        <p className="mt-4 font-serif text-[17px] leading-relaxed text-[var(--fg-secondary)]">
          {t("landing.capabilities.subtitle")}
        </p>
      </div>
      <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {CAP_ITEMS.map(({ key, Icon, tone }) => (
          <div
            key={key}
            className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--bg-raised)] p-6 shadow-[var(--shadow-sm)]"
          >
            <div
              className={cn(
                "mb-4 flex h-[46px] w-[46px] items-center justify-center rounded-[12px]",
                TONE_BG[tone],
                TONE_FG[tone],
              )}
            >
              <Icon size={21} aria-hidden="true" />
            </div>
            <h3 className="font-serif text-xl font-medium tracking-[-0.01em] text-[var(--fg-primary)]">
              {t(`landing.capabilities.items.${key}.title`)}
            </h3>
            <p className="mt-2 font-serif text-[15px] leading-relaxed text-[var(--fg-secondary)]">
              {t(`landing.capabilities.items.${key}.body`)}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════
// 4. MODULES
// ════════════════════════════════════════════════════════════════════
interface ModuleItem {
  readonly key: string;
  readonly Icon: LucideIcon;
}
const MODULE_ITEMS: readonly ModuleItem[] = [
  { key: "requests", Icon: Inbox },
  { key: "leave", Icon: CalendarDays },
  { key: "payroll", Icon: Wallet },
  { key: "org", Icon: Target },
  { key: "people", Icon: Users },
  { key: "reports", Icon: BarChart3 },
];

function Modules(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <section className="px-6 py-[72px] lg:px-8">
      <div className="mx-auto mb-11 max-w-[720px] text-center">
        <Eyebrow className="mb-3.5">{t("landing.modules.eyebrow")}</Eyebrow>
        <DisplayHeading
          lead={t("landing.modules.title_lead")}
          accent={t("landing.modules.title_accent")}
          className="text-[clamp(1.9rem,3.8vw,2.6rem)]"
        />
      </div>
      <div className="mx-auto grid max-w-[1140px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {MODULE_ITEMS.map(({ key, Icon }) => (
          <div
            key={key}
            className="rounded-[16px] border border-[var(--border-subtle)] bg-[var(--bg-raised)] p-6 shadow-[var(--shadow-xs)]"
          >
            <div className="mb-3.5 flex h-10 w-10 items-center justify-center rounded-[10px] bg-[var(--mkt-tint-accent)] text-[var(--accent-hover)]">
              <Icon size={19} aria-hidden="true" />
            </div>
            <h3 className="font-serif text-lg font-medium text-[var(--fg-primary)]">
              {t(`landing.modules.items.${key}.title`)}
            </h3>
            <p className="mt-1.5 font-serif text-sm leading-relaxed text-[var(--fg-secondary)]">
              {t(`landing.modules.items.${key}.body`)}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════
// 3. HOW IT WORKS
// ════════════════════════════════════════════════════════════════════
// The brochure's own sequence: meet, migrate from Excel or a dump with a parallel
// run, then a short hands-on training. No timeline is promised in days or weeks;
// the transition length is planned in the first meeting.
const HOW_STEPS = ["meet", "import", "launch"] as const;

function HowItWorks(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <section className="border-y border-[var(--border-subtle)] bg-[var(--bg-raised)] px-6 py-[76px] lg:px-8">
      <div className="mx-auto mb-11 max-w-[720px] text-center">
        <Eyebrow className="mb-3.5">{t("landing.how.eyebrow")}</Eyebrow>
        <DisplayHeading
          lead={t("landing.how.title_lead")}
          accent={t("landing.how.title_accent")}
          className="text-[clamp(1.9rem,3.8vw,2.6rem)]"
        />
      </div>
      <div className="mx-auto grid max-w-[1080px] grid-cols-1 gap-6 md:grid-cols-3">
        {HOW_STEPS.map((key) => (
          <div
            key={key}
            className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-7 shadow-[var(--shadow-xs)]"
          >
            <p className="mb-3.5 font-mono text-[13px] font-semibold text-[var(--accent)]">
              {t(`landing.how.steps.${key}.num`)}
            </p>
            <h3 className="font-serif text-[22px] font-medium text-[var(--fg-primary)]">
              {t(`landing.how.steps.${key}.title`)}
            </h3>
            <p className="mt-2.5 font-serif text-[15px] leading-relaxed text-[var(--fg-secondary)]">
              {t(`landing.how.steps.${key}.body`)}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════
// 5. EDITORIAL QUOTE + PRODUCT PROPERTIES
// ════════════════════════════════════════════════════════════════════
// The drawing's figures (%92 approval, 1.4-day decision, 1,248 users) had no
// measured source; a footnote did not make them true. Each card now states a
// property the shipped product has, with no number.
const QUOTE_PROPERTIES = ["access", "deployment", "languages"] as const;
// An odd card count leaves a hole in a two-column grid; the last card spans both
// columns so removing a card never leaves an empty slot.
const QUOTE_PROPERTIES_ODD = QUOTE_PROPERTIES.length % 2 === 1;

function QuoteAndProperties(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <section className="grid grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14 lg:px-8">
      <div className="mx-auto w-full max-w-[1200px] lg:mx-0">
        <Eyebrow className="mb-4">{t("landing.quote.eyebrow")}</Eyebrow>
        <blockquote className="m-0 font-serif text-[clamp(1.5rem,3vw,1.875rem)] font-normal leading-[1.35] tracking-[-0.01em] text-[var(--fg-primary)]">
          {t("landing.quote.body")}
        </blockquote>
        <div className="mt-7 flex items-center gap-3">
          <div className="flex h-[46px] w-[46px] items-center justify-center rounded-full bg-[var(--accent)] font-sans text-[15px] font-semibold text-[var(--fg-on-accent)]">
            {t("landing.quote.author_initials")}
          </div>
          <div>
            <p className="font-sans text-[15px] font-semibold text-[var(--fg-primary)]">
              {t("landing.quote.author_name")}
            </p>
            <p className="font-serif text-[13.5px] italic text-[var(--fg-muted)]">
              {t("landing.quote.author_role")}
            </p>
          </div>
        </div>
      </div>
      <div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {QUOTE_PROPERTIES.map((key, i) => (
            <div
              key={key}
              className={cn(
                "rounded-[16px] border border-[var(--border-subtle)] bg-[var(--bg-raised)] p-6 shadow-[var(--shadow-xs)]",
                QUOTE_PROPERTIES_ODD && i === QUOTE_PROPERTIES.length - 1 && "sm:col-span-2",
              )}
            >
              <p className="font-sans text-[16px] font-semibold text-[var(--fg-primary)]">
                {t(`landing.quote.properties.${key}.title`)}
              </p>
              <p className="mt-1.5 font-serif text-[15px] leading-snug text-[var(--fg-secondary)]">
                {t(`landing.quote.properties.${key}.body`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════
// 6. CTA BAND
// ════════════════════════════════════════════════════════════════════
function CtaBand(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <section className="mx-6 mb-[72px] lg:mx-8">
      <div className="relative mx-auto max-w-[1200px] overflow-hidden rounded-[28px] bg-[var(--color-accent-950)] px-8 py-16 text-[var(--mkt-on-deep)] lg:px-14">
        <div className="relative max-w-[640px]">
          <h2 className="m-0 font-display text-[clamp(2rem,4vw,2.875rem)] font-semibold leading-[1.05] tracking-[-0.03em]">
            {t("landing.cta.title_lead")}{" "}
            <span className="font-serif font-normal italic text-[var(--color-accent-300)]">
              {t("landing.cta.title_accent")}
            </span>{" "}
            {t("landing.cta.title_after_accent")}
          </h2>
          <p className="mt-4.5 font-serif text-lg leading-relaxed text-[var(--mkt-on-deep)]/80">
            {t("landing.cta.subtitle")}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild variant="primary">
              <Link to={DEMO_REQUEST_PATH} className="no-underline">
                {t("landing.cta.primary")} <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </Button>
            <Link
              to="/pricing"
              className="inline-flex items-center rounded-[8px] border border-[var(--mkt-on-deep)]/25 px-6 py-3 font-sans text-[15px] font-medium text-[var(--mkt-on-deep)] no-underline transition-colors hover:bg-[var(--mkt-on-deep)]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mkt-on-deep)]"
            >
              {t("landing.cta.secondary")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Page assembly ───────────────────────────────────────────────────
export function LandingPage(): React.ReactElement {
  return (
    <>
      <Hero />
      <Capabilities />
      <HowItWorks />
      <Modules />
      <QuoteAndProperties />
      <CtaBand />
    </>
  );
}
